import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MarketplaceService } from '@purespectrum1/ui/marketplace';
import { BuyerSurveyResponse } from '@purespectrum1/ui/marketplace/shared/interfaces/survey.interface';

import { AuthState } from '@purespectrum1/ui/marketplace/shared/interfaces/auth.interface';
import { ModalService } from '@purespectrum1/ui/modal';
import { AuthService } from '../../auth/auth.service';
import { MultiBuyerStateService } from '../../shared/services/multi-buyer/multi-buyer-state.service';
import { ServiceAccountModalComponent } from '../../layout/header/user-menu/service-account-modal/service-account-modal.component';
import { MultiBuyerModalComponent } from '../../shared/ui/multi-buyer-modal/multi-buyer-modal.component';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { MultiBuyerSession } from '../../shared/services/multi-buyer/multi-buyer-session';
import { SurveyMetaDataService } from '../../shared/services/survey-meta-data/survey-meta-data.service';
import { PlatformSwitcherPageController } from '../../shared/services/platform-switcher/platform-switcher-page-controller';

@Component({
  selector: 'ps-survey-launch',
  templateUrl: './survey-launch.component.html',
  styleUrls: ['./survey-launch.component.css'],
  providers: [PlatformSwitcherPageController],
})
export class SurveyLaunchComponent implements OnInit, OnDestroy {
  private _subscriptions = new Subscription();
  public surveyId!: number;
  public auth: AuthState = this._auth.getMarketplaceAuth;
  public buyerSurvey: BuyerSurveyResponse = {} as BuyerSurveyResponse;
  public isMultiBuyerParent: boolean = false;
  public umbrellaCompanyId?: number;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _auth: AuthService,
    private _marketplaceService: MarketplaceService,
    private _modal: ModalService,
    private readonly _buyerApiService: BuyerApiService,
    private readonly _multiBuyerSession: MultiBuyerSession,
    private readonly _multiBuyerState: MultiBuyerStateService,
    private readonly _surveyMetaDataService: SurveyMetaDataService,
    private readonly _platformSwitcherPage: PlatformSwitcherPageController
  ) {}

  ngOnInit(): void {
    // Set once, same reasoning as CreateSurveysComponent: this component is
    // only ever entered fresh (launch-survey/:id is its own route, not a
    // param change on an existing one).
    this._platformSwitcherPage.setSwitcherMatchFromUrl(this._router.url);

    this._subscriptions.add(
      this._route.params.subscribe((params) => {
        this.surveyId = params['id'];
      })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getisFormDirty()
        .subscribe((isFormDirty: boolean) => {
          this._platformSwitcherPage.isFormDirty = isFormDirty;
        })
    );

    this._subscriptions.add(
      this._auth.loggedInAsServiceBuyer$.subscribe((data) => {
        this.auth = this._auth.getMarketplaceAuth;
      })
    );

    this._subscriptions.add(
      this._multiBuyerState.selectedMultiBuyerCompany$.subscribe((company) => {
        this.umbrellaCompanyId = company?.parentCompany;
      })
    );

    this.isMultiBuyerParent = this._auth.isLoggedInAsMultiBuyer();

    if (this._auth.isSelectedMultiBuyerCompany()) {
      this.auth = this._auth.getMarketplaceAuth;
    }

    this._subscriptions.add(
      this._marketplaceService
        .onUpdateSurveyComplete()
        .subscribe((response) => {
          if (response.survey?.ps_survey_id && response.action === 'LiveEdit') {
            this._router.navigateByUrl(
              `/dashboard/${response.survey.ps_survey_id}`
            );
          }
        })
    );

    this._subscriptions.add(
      this._buyerApiService
        .getSurvey(this.surveyId, 0, false)
        .subscribe((response) => {
          this.buyerSurvey = response;

          if (this.isMultiBuyerParent) {
            this.setSelectedMultiBuyer();
          }
        })
    );
  }

  setSelectedMultiBuyer() {
    const cmpId =
      this._multiBuyerState.selectedMultiBuyerCompany?.parentCompany ??
      this.auth.user?.cmp;
    const targetBuyerId = this.buyerSurvey.buyer_company;
    this._multiBuyerSession.selectBuyer(cmpId!, targetBuyerId);
  }

  goToDashboard(close: boolean) {
    if (close) {
      this._router.navigateByUrl('/dashboard');
    }
  }

  selectBuyer(open: boolean) {
    if (open) {
      this._modal.open(ServiceAccountModalComponent, {});
    }
  }
  selectMultiBuyer() {
    const cmpId =
      this._multiBuyerState.selectedMultiBuyerCompany?.parentCompany ??
      this.auth.user?.cmp;
    this._modal.open(MultiBuyerModalComponent, {
      data: { cmpId: cmpId },
    });
  }

  get switcherMatch() {
    return this._platformSwitcherPage.switcherMatch;
  }

  get canSwitchUi(): boolean {
    return this._platformSwitcherPage.canSwitchUi;
  }

  switchToPhoenix(): void {
    this._platformSwitcherPage.switchToPhoenix();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
